import React, { useState, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronRight,
  FileText,
  Pencil,
  Eye,
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  Layers,
  Plus,
  Calendar,
  Save,
  X,
  Settings2,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
// Radix tooltip imports kept for potential future use but not used in this component
// import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

// Pure CSS tooltip – no JS state, hides immediately on cursor leave
function CSSTooltip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="relative group/tip inline-flex">
      {children}
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-900 text-[11px] leading-tight text-gray-100 whitespace-nowrap opacity-0 scale-95 group-hover/tip:opacity-100 group-hover/tip:scale-100 transition-all duration-100 z-[400]">
        {label}
      </span>
    </span>
  )
}
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'

import EvidencePortfolio from './EvidencePortfolio'

import { calculateProgress } from '@/utils/kpiProgress'
import type { MeasurementDirection, MeasurementMode } from '@/types/kpi'
import type { EnhancedEvidenceEntry, KPAData } from '@/types/evidence'

// ─── Types ───────────────────────────────────────────────────────────────────

interface ObjectiveMatrixViewProps {
  kpas: KPAData[]
  employeeId: string
  employeeName: string
  employeeRole: string
  reviewPeriod?: 'MIDYEAR' | 'YEAREND'

  // Evidence handlers
  evidence: EnhancedEvidenceEntry[]
  onAddEvidence: (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => Promise<EnhancedEvidenceEntry | undefined> | void
  onUpdateEvidence?: (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => void
  onUpdateEvidenceClassification?: (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => void
  onRefreshEvidence?: () => Promise<void>
  onDeleteEvidence: (evidenceId: string) => void
  onUploadDocument: (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => Promise<void>

  // Objective handlers
  onCreateObjective: (objective: any) => void
  onUpdateObjective: (objectiveId: number, updates: any) => void | Promise<void>
  onDeleteObjective: (objectiveId: number) => void
  onViewObjectiveDetails: (objective: any) => void
  onConfigureObjectives?: (initialTab?: 'pending' | 'active' | 'rejected' | 'allocate') => void

  // KPI handlers
  onViewKPIDetails: (kpi: any) => void

  // Navigation: switch to card view + navigate to a KPA tab
  onSwitchToCardView?: (kpaId?: string) => void
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Lifecycle/workflow status badge only — RAG health statuses (on-track / at-risk /
// off-track) are intentionally not surfaced to users on this fresh platform.
function getStatusBadge(status: string) {
  const map: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    active: { label: 'Active', variant: 'default' },
    pending_approval: { label: 'Pending', variant: 'secondary' },
    pending: { label: 'Pending', variant: 'secondary' },
    draft: { label: 'Draft', variant: 'outline' },
    completed: { label: 'Completed', variant: 'default' },
    rejected: { label: 'Rejected', variant: 'destructive' },
  }
  const found = map[status] || { label: status, variant: 'outline' as const }
  return <Badge variant={found.variant} className="text-xs whitespace-nowrap">{found.label}</Badge>
}

function DirectionIcon({ direction }: { direction: string }) {
  if (direction === 'lower_is_better') return <TrendingDown className="h-3 w-3 text-blue-500" />
  if (direction === 'higher_is_better') return <TrendingUp className="h-3 w-3 text-green-500" />
  return <Minus className="h-3 w-3 text-gray-400" />
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ObjectiveMatrixView({
  kpas,
  employeeId,
  evidence,
  onAddEvidence,
  onUpdateEvidence,
  onRefreshEvidence,
  onDeleteEvidence,
  onUploadDocument,
  onConfigureObjectives,
  onUpdateObjective,
  onSwitchToCardView,
}: ObjectiveMatrixViewProps) {
  // ── State ────────────────────────────────────────────────────────────────
  const [expandedKPAs, setExpandedKPAs] = useState<Set<string>>(() => new Set(kpas.map(k => k.id)))
  const [expandedObjectives, setExpandedObjectives] = useState<Set<number>>(new Set())

  // Evidence Sheet state
  const [evidenceSheetOpen, setEvidenceSheetOpen] = useState(false)
  const [evidenceContext, setEvidenceContext] = useState<{
    objectiveId: number
    objectiveTitle: string
    kpaId: string
    kpaName: string
  } | null>(null)

  // Objective Detail Sheet state
  const [objectiveSheetOpen, setObjectiveSheetOpen] = useState(false)
  const [objectiveSheetData, setObjectiveSheetData] = useState<{
    objective: KPAData['individualObjectives'][0]
    kpa: KPAData
  } | null>(null)
  const [isEditingObjective, setIsEditingObjective] = useState(false)
  const [editForm, setEditForm] = useState<{
    title: string
    description: string
    start_date: string
    end_date: string
  }>({ title: '', description: '', start_date: '', end_date: '' })
  const [isSavingObjective, setIsSavingObjective] = useState(false)

  // ── Derived Data ─────────────────────────────────────────────────────────
  const sortedKPAs = useMemo(() =>
    [...kpas].sort((a, b) => ((a as any).sort_order || 999) - ((b as any).sort_order || 999)),
    [kpas]
  )

  // Calculate objective progress from KPIs (matches EmployeeKPAView logic)
  const calcObjectiveProgress = useCallback((objective: KPAData['individualObjectives'][0]) => {
    if (!objective.kpis || objective.kpis.length === 0) return objective.progress || 0
    const total = objective.kpis.reduce((sum, kpi) => {
      const mode = ((kpi as any).measurement_mode || 'numeric') as MeasurementMode
      const direction = ((kpi as any).measurement_direction || 'higher_is_better') as MeasurementDirection
      const res = calculateProgress(kpi.current_value ?? undefined, kpi.target_value, direction, mode)
      return sum + res.progress
    }, 0)
    return Math.round(total / objective.kpis.length)
  }, [])

  // Calculate KPA progress
  const calcKPAProgress = useCallback((kpa: KPAData) => {
    const active = kpa.individualObjectives.filter(o => o.status !== 'rejected')
    if (active.length === 0) return kpa.averageProgress || 0
    const total = active.reduce((s, o) => s + calcObjectiveProgress(o), 0)
    return Math.round(total / active.length)
  }, [calcObjectiveProgress])

  // Aggregate stats
  const stats = useMemo(() => {
    const totalObj = sortedKPAs.reduce((s, k) => s + k.individualObjectives.filter(o => o.status !== 'rejected').length, 0)
    const totalKPIs = sortedKPAs.reduce((s, k) => s + k.underlyingKPIs.length, 0)
    const totalEvidence = sortedKPAs.reduce((s, k) => s + k.totalEvidence, 0)
    const avg = sortedKPAs.length > 0
      ? Math.round(sortedKPAs.reduce((s, k) => s + calcKPAProgress(k), 0) / sortedKPAs.length)
      : 0
    return { totalObj, totalKPIs, totalEvidence, avg }
  }, [sortedKPAs, calcKPAProgress])

  // Build availableKPIs for EvidencePortfolio (so it can resolve KPI IDs to names)
  const availableKPIs = useMemo(() => {
    const kpis: Array<{ id: string; name: string; objectiveTitle?: string; current_value?: number; target_value?: number; unit?: string; measurement_history?: string | null }> = []
    sortedKPAs.forEach(kpa => {
      kpa.individualObjectives.filter(o => o.status !== 'rejected').forEach(obj => {
        (obj.kpis || []).forEach(kpi => {
          if (!kpis.find(k => k.id === kpi.id)) {
            kpis.push({
              id: kpi.id,
              name: kpi.name,
              objectiveTitle: obj.title,
              current_value: kpi.current_value,
              target_value: kpi.target_value,
              unit: kpi.unit,
              measurement_history: (kpi as any).measurement_history ?? null,
            })
          }
        })
      })
      kpa.underlyingKPIs?.forEach(kpi => {
        if (!kpis.find(k => k.id === kpi.id)) {
          kpis.push({
            id: kpi.id,
            name: kpi.name,
            current_value: kpi.current_value ?? undefined,
            target_value: kpi.target_value,
            unit: kpi.unit,
            measurement_history: (kpi as any).measurement_history ?? null,
          })
        }
      })
    })
    return kpis
  }, [sortedKPAs])

  // Evidence for current sheet context
  const contextEvidence = useMemo(() => {
    if (!evidenceContext) return []
    const objId = String(evidenceContext.objectiveId)
    return evidence.filter(e =>
      e.objectiveId === objId ||
      e.kpaId === evidenceContext.kpaId
    )
  }, [evidence, evidenceContext])

  // ── Toggle helpers ───────────────────────────────────────────────────────
  const toggleKPA = (kpaId: string) => {
    setExpandedKPAs(prev => {
      const next = new Set(prev)
      next.has(kpaId) ? next.delete(kpaId) : next.add(kpaId)
      return next
    })
  }

  const toggleObjective = (objId: number) => {
    setExpandedObjectives(prev => {
      const next = new Set(prev)
      next.has(objId) ? next.delete(objId) : next.add(objId)
      return next
    })
  }

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleOpenEvidence = (objective: KPAData['individualObjectives'][0], kpa: KPAData) => {
    setEvidenceContext({
      objectiveId: objective.id,
      objectiveTitle: objective.title,
      kpaId: kpa.id,
      kpaName: kpa.display_name || kpa.name,
    })
    setEvidenceSheetOpen(true)
  }

  const handleEditObjective = (objective: KPAData['individualObjectives'][0], kpa: KPAData) => {
    setObjectiveSheetData({ objective, kpa })
    setIsEditingObjective(false)
    setEditForm({
      title: objective.title,
      description: objective.description || '',
      start_date: objective.start_date?.split('T')[0] || '',
      end_date: objective.end_date?.split('T')[0] || '',
    })
    setObjectiveSheetOpen(true)
  }

  const handleViewInCard = (kpaId: string) => {
    onSwitchToCardView?.(kpaId)
  }

  // Count evidence for an objective
  const getEvidenceCount = (objectiveId: number, kpaId: string) => {
    const objId = String(objectiveId)
    return evidence.filter(e => e.objectiveId === objId || e.kpaId === kpaId).length
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
      <div className="space-y-4">
      {/* Summary Bar */}
      <div className="bg-gradient-to-r from-heritage-blue/5 to-teal-50/50 rounded-xl border border-heritage-blue/10 p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-heritage-blue/10 flex items-center justify-center">
              <Target className="h-5 w-5 text-heritage-blue" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalObj}</p>
              <p className="text-xs text-gray-500">Objectives</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100/60 flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalKPIs}</p>
              <p className="text-xs text-gray-500">KPIs</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100/60 flex items-center justify-center">
              <FileText className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalEvidence}</p>
              <p className="text-xs text-gray-500">Impact Items</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100/60 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.avg}%</p>
              <p className="text-xs text-gray-500">Avg Progress</p>
            </div>
          </div>
        </div>
      </div>

      {/* Matrix Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/80">
              <TableHead className="w-[28px] pl-3 pr-0" />
              <TableHead className="font-semibold text-gray-700 text-xs uppercase tracking-wide">Objective</TableHead>
              <TableHead className="font-semibold text-gray-700 text-xs uppercase tracking-wide w-[90px]">Status</TableHead>
              <TableHead className="font-semibold text-gray-700 text-xs uppercase tracking-wide w-[140px]">Progress</TableHead>
              <TableHead className="font-semibold text-gray-700 text-xs uppercase tracking-wide w-[80px] text-center">KPIs</TableHead>
              <TableHead className="font-semibold text-gray-700 text-xs uppercase tracking-wide w-[80px] text-center">Impact</TableHead>
              <TableHead className="font-semibold text-gray-700 text-xs uppercase tracking-wide w-[120px] text-right pr-4">Actions</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {sortedKPAs.map((kpa) => {
              const kpaProgress = calcKPAProgress(kpa)
              const activeObjectives = kpa.individualObjectives.filter(o => o.status !== 'rejected')
              const isKPAExpanded = expandedKPAs.has(kpa.id)

              return (
                <AnimatePresence key={kpa.id} initial={false}>
                  {/* KPA Group Header Row */}
                  <TableRow
                    className="bg-heritage-blue/[0.03] hover:bg-heritage-blue/[0.06] cursor-pointer border-b border-gray-200"
                    onClick={() => toggleKPA(kpa.id)}
                  >
                    <TableCell className="pl-3 pr-0 py-3">
                      <motion.div animate={{ rotate: isKPAExpanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      </motion.div>
                    </TableCell>
                    <TableCell className="py-3" colSpan={2}>
                      <div className="flex items-center gap-2">
                        <Layers className="h-4 w-4 text-heritage-blue flex-shrink-0" />
                        <span className="font-semibold text-gray-900 text-sm">{kpa.display_name || kpa.name}</span>
                        <span className="text-xs text-gray-500 ml-1">
                          {activeObjectives.length} obj · {kpa.underlyingKPIs.length} KPIs
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="flex items-center gap-2">
                        <Progress value={kpaProgress} className="h-2 flex-1" />
                        <span className="text-xs font-medium text-gray-700 w-8 text-right">{kpaProgress}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center py-3">
                      <span className="text-xs font-medium text-gray-600">{kpa.underlyingKPIs.length}</span>
                    </TableCell>
                    <TableCell className="text-center py-3">
                      <span className="text-xs font-medium text-gray-600">{kpa.totalEvidence}</span>
                    </TableCell>
                    <TableCell className="text-right pr-4 py-3">
                      <CSSTooltip label="View in card mode">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={(e) => { e.stopPropagation(); handleViewInCard(kpa.id) }}
                        >
                          <Eye className="h-3.5 w-3.5 text-gray-500" />
                        </Button>
                      </CSSTooltip>
                    </TableCell>
                  </TableRow>

                  {/* Objective Rows */}
                  {isKPAExpanded && activeObjectives.map((objective) => {
                    const objProgress = calcObjectiveProgress(objective)
                    const isObjExpanded = expandedObjectives.has(objective.id)
                    const evCount = getEvidenceCount(objective.id, kpa.id)
                    const kpiCount = objective.kpis?.length || 0

                    return (
                      <AnimatePresence key={objective.id} initial={false}>
                        {/* Objective Row */}
                        <TableRow
                          className="hover:bg-gray-50/80 border-b border-gray-100"
                        >
                          <TableCell className="pl-5 pr-0 py-2.5">
                            {kpiCount > 0 && (
                              <button
                                onClick={() => toggleObjective(objective.id)}
                                className="p-0.5 rounded hover:bg-gray-200/60 transition-colors"
                              >
                                <motion.div animate={{ rotate: isObjExpanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
                                  <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                                </motion.div>
                              </button>
                            )}
                          </TableCell>
                          <TableCell className="py-2.5">
                            <div className="pl-4">
                              <p className="text-sm font-medium text-gray-800 leading-snug">{objective.title}</p>
                              {objective.strategic_objective && (
                                <p className="text-xs text-gray-400 mt-0.5 truncate max-w-md">
                                  {objective.strategic_objective.display_name || objective.strategic_objective.name}
                                  {objective.strategic_initiative && ` → ${objective.strategic_initiative.name}`}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-2.5">{getStatusBadge(objective.status)}</TableCell>
                          <TableCell className="py-2.5">
                            <div className="flex items-center gap-2">
                              <Progress value={objProgress} className="h-1.5 flex-1" />
                              <span className="text-xs font-medium text-gray-600 w-8 text-right">{objProgress}%</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center py-2.5">
                            <div className="flex items-center justify-center gap-0.5">
                              <span className="text-xs font-medium text-gray-600">{kpiCount}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center py-2.5">
                            <span className={`text-xs font-medium ${evCount > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                              {evCount}
                            </span>
                          </TableCell>
                          <TableCell className="text-right pr-4 py-2.5">
                            <div className="flex items-center justify-end gap-1">
                              <CSSTooltip label="Impact Portfolio">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleOpenEvidence(objective, kpa)}
                                >
                                  <FileText className="h-3.5 w-3.5 text-purple-500" />
                                </Button>
                              </CSSTooltip>
                              <CSSTooltip label="View / Edit Objective">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleEditObjective(objective, kpa)}
                                >
                                  <Pencil className="h-3.5 w-3.5 text-heritage-blue" />
                                </Button>
                              </CSSTooltip>
                              <CSSTooltip label="View in KPA tab">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleViewInCard(kpa.id)}
                                >
                                  <Eye className="h-3.5 w-3.5 text-gray-400" />
                                </Button>
                              </CSSTooltip>
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* KPI Sub-rows (expanded) */}
                        {isObjExpanded && (objective.kpis || []).map((kpi) => {
                          const mode = ((kpi as any).measurement_mode || 'numeric') as MeasurementMode
                          const direction = ((kpi as any).measurement_direction || 'higher_is_better') as MeasurementDirection
                          const kpiResult = calculateProgress(kpi.current_value ?? undefined, kpi.target_value, direction, mode)

                          return (
                            <TableRow
                              key={kpi.id}
                              className="bg-gray-50/40 hover:bg-gray-100/50 border-b border-gray-50"
                            >
                              <TableCell className="pl-5 pr-0 py-1.5" />
                              <TableCell className="py-1.5">
                                <div className="pl-10 flex items-center gap-2">
                                  <BarChart3 className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                  <span className="text-xs text-gray-600">{kpi.name}</span>
                                  <DirectionIcon direction={direction} />
                                </div>
                              </TableCell>
                              <TableCell className="py-1.5" />
                              <TableCell className="py-1.5">
                                <div className="flex items-center gap-2">
                                  <Progress value={kpiResult.progress} className="h-1 flex-1" />
                                  <span className="text-[11px] text-gray-500 w-8 text-right">{kpiResult.progress}%</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center py-1.5">
                                <span className="text-[11px] text-gray-500">
                                  {kpi.current_value ?? '–'} / {kpi.target_value} {kpi.unit}
                                </span>
                              </TableCell>
                              <TableCell className="py-1.5" />
                              <TableCell className="py-1.5" />
                            </TableRow>
                          )
                        })}
                      </AnimatePresence>
                    )
                  })}
                </AnimatePresence>
              )
            })}
          </TableBody>

          {/* Summary Footer */}
          <TableFooter>
            <TableRow className="bg-gray-50/60">
              <TableCell className="pl-3" />
              <TableCell className="font-semibold text-gray-700 text-sm">
                Total across {sortedKPAs.length} KPAs
              </TableCell>
              <TableCell />
              <TableCell>
                <div className="flex items-center gap-2">
                  <Progress value={stats.avg} className="h-2 flex-1" />
                  <span className="text-xs font-semibold text-gray-700 w-8 text-right">{stats.avg}%</span>
                </div>
              </TableCell>
              <TableCell className="text-center">
                <span className="text-xs font-semibold text-gray-700">{stats.totalKPIs}</span>
              </TableCell>
              <TableCell className="text-center">
                <span className="text-xs font-semibold text-gray-700">{stats.totalEvidence}</span>
              </TableCell>
              <TableCell className="text-right pr-4">
                {onConfigureObjectives && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1 border-heritage-blue/30 text-heritage-blue hover:bg-heritage-blue/5"
                    onClick={() => onConfigureObjectives('allocate')}
                  >
                    <Plus className="h-3 w-3" />
                    Add
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>

      {/* Objective Detail Sheet */}
      <Sheet open={objectiveSheetOpen} onOpenChange={(open) => {
        setObjectiveSheetOpen(open)
        if (!open) { setIsEditingObjective(false); setIsSavingObjective(false) }
      }}>
        <SheetContent side="right" className="!w-[95vw] sm:!w-[600px] md:!w-[720px] !max-w-none overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-heritage-blue" />
              Objective Details
            </SheetTitle>
            <SheetDescription>
              {objectiveSheetData
                ? `${objectiveSheetData.kpa.display_name || objectiveSheetData.kpa.name}`
                : ''}
            </SheetDescription>
          </SheetHeader>

          {objectiveSheetData && (() => {
            const { objective, kpa } = objectiveSheetData
            const objProgress = calcObjectiveProgress(objective)

            return (
              <div className="space-y-5">
                {/* Action Bar */}
                <div className="flex items-center justify-between">
                  {!isEditingObjective ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => setIsEditingObjective(true)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsEditingObjective(false)}
                        disabled={isSavingObjective}
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        disabled={isSavingObjective}
                        onClick={async () => {
                          setIsSavingObjective(true)
                          try {
                            // Include edited_by_role so the backend triggers the
                            // status transition (active → pending_approval).
                            // Preserve existing KPI and strategic context by passing
                            // them through — the backend only processes fields that
                            // are non-null, so omitting kpis keeps them untouched.
                            const updates: Record<string, any> = {
                              title: editForm.title,
                              description: editForm.description,
                              start_date: editForm.start_date || null,
                              end_date: editForm.end_date || null,
                              edited_by_role: 'employee',
                            }
                            // Preserve L1-L3 strategic context so it isn't lost
                            if (objective.strategic_objective?.id) {
                              updates.strategic_objective_id = objective.strategic_objective.id
                            }
                            if (objective.strategic_initiative?.id) {
                              updates.strategic_initiative_id = objective.strategic_initiative.id
                            }
                            if (objective.operational_initiative?.id) {
                              updates.operational_initiative_id = objective.operational_initiative.id
                            }
                            await onUpdateObjective(objective.id, updates)
                            // Only close on success — parent handler shows the toast
                            setIsEditingObjective(false)
                            setObjectiveSheetOpen(false)
                          } catch {
                            // Parent handler already shows error toast — keep sheet open
                          } finally {
                            setIsSavingObjective(false)
                          }
                        }}
                      >
                        {isSavingObjective ? (
                          <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving...</>
                        ) : (
                          <><Save className="h-3.5 w-3.5 mr-1" /> Save Changes</>
                        )}
                      </Button>
                    </div>
                  )}
                  {onConfigureObjectives && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1.5 text-heritage-blue"
                      onClick={() => {
                        setObjectiveSheetOpen(false)
                        onConfigureObjectives('active')
                      }}
                    >
                      <Settings2 className="h-3.5 w-3.5" />
                      Full Config
                    </Button>
                  )}
                </div>

                {/* Status & Progress Summary */}
                <div className="flex items-center gap-3">
                  {getStatusBadge(objective.status)}
                  <div className="flex-1 flex items-center gap-2">
                    <Progress value={objProgress} className="h-2 flex-1" />
                    <span className="text-sm font-semibold text-gray-700">{objProgress}%</span>
                  </div>
                </div>

                <Separator />

                {/* Title */}
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Title</Label>
                  {isEditingObjective ? (
                    <Input
                      value={editForm.title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      className="mt-1"
                    />
                  ) : (
                    <p className="text-sm mt-1 text-gray-900 font-medium">{objective.title}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Description</Label>
                  {isEditingObjective ? (
                    <Textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      className="mt-1 min-h-[80px]"
                    />
                  ) : (
                    <p className="text-sm mt-1 text-gray-600">{objective.description || 'No description'}</p>
                  )}
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold text-gray-700">Start Date</Label>
                    {isEditingObjective ? (
                      <Input
                        type="date"
                        value={editForm.start_date}
                        onChange={(e) => setEditForm(prev => ({ ...prev, start_date: e.target.value }))}
                        className="mt-1"
                      />
                    ) : (
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {objective.start_date ? new Date(objective.start_date).toLocaleDateString() : '—'}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-gray-700">End Date</Label>
                    {isEditingObjective ? (
                      <Input
                        type="date"
                        value={editForm.end_date}
                        onChange={(e) => setEditForm(prev => ({ ...prev, end_date: e.target.value }))}
                        className="mt-1"
                      />
                    ) : (
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {objective.end_date ? new Date(objective.end_date).toLocaleDateString() : '—'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Strategic Context */}
                {(objective.strategic_objective || objective.strategic_initiative) && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Layers className="h-4 w-4" />
                        Strategic Context
                      </Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {objective.strategic_objective && (
                          <Badge variant="secondary" className="text-xs">
                            {objective.strategic_objective.display_name || objective.strategic_objective.name}
                          </Badge>
                        )}
                        {objective.strategic_initiative && (
                          <Badge variant="outline" className="text-xs">
                            {objective.strategic_initiative.name}
                          </Badge>
                        )}
                        {objective.operational_initiative && (
                          <Badge variant="outline" className="text-xs bg-gray-50">
                            {objective.operational_initiative.name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* KPIs */}
                <Separator />
                <div>
                  <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                    <BarChart3 className="h-4 w-4" />
                    Key Performance Indicators ({objective.kpis?.length || 0})
                  </Label>
                  {(!objective.kpis || objective.kpis.length === 0) ? (
                    <div className="text-center py-6 border-2 border-dashed rounded-lg">
                      <Target className="h-6 w-6 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-400">No KPIs defined</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {objective.kpis.map((kpi, idx) => {
                        const mode = ((kpi as any).measurement_mode || 'numeric') as MeasurementMode
                        const direction = ((kpi as any).measurement_direction || 'higher_is_better') as MeasurementDirection
                        const kpiResult = calculateProgress(kpi.current_value ?? undefined, kpi.target_value, direction, mode)

                        return (
                          <div key={kpi.id} className="border rounded-lg p-3 bg-gray-50/50">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="text-sm font-medium text-gray-800">{kpi.name}</p>
                                {kpi.description && (
                                  <p className="text-xs text-gray-500 mt-0.5">{kpi.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Progress value={kpiResult.progress} className="h-1.5 flex-1" />
                              <span className="text-xs font-medium text-gray-600">{kpiResult.progress}%</span>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span>Current: <strong>{kpi.current_value ?? '–'}</strong></span>
                              <span>Target: <strong>{kpi.target_value}</strong></span>
                              <span>{kpi.unit}</span>
                              <DirectionIcon direction={direction} />
                              {kpi.frequency && <span className="capitalize">{kpi.frequency}</span>}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Quick Actions Footer */}
                <Separator />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1.5"
                    onClick={() => {
                      setObjectiveSheetOpen(false)
                      handleOpenEvidence(objective, kpa)
                    }}
                  >
                    <FileText className="h-3.5 w-3.5 text-purple-500" />
                    Impact Portfolio
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1.5"
                    onClick={() => {
                      setObjectiveSheetOpen(false)
                      handleViewInCard(kpa.id)
                    }}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Card View
                  </Button>
                </div>
              </div>
            )
          })()}
        </SheetContent>
      </Sheet>

      {/* Evidence Sheet */}
      <Sheet open={evidenceSheetOpen} onOpenChange={setEvidenceSheetOpen}>
        <SheetContent side="right" className="!w-[95vw] sm:!w-[600px] md:!w-[720px] !max-w-none overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-lg">
              Impact Portfolio
            </SheetTitle>
            <SheetDescription>
              {evidenceContext
                ? `${evidenceContext.kpaName} — ${evidenceContext.objectiveTitle}`
                : 'Select an objective to view impact portfolio'}
            </SheetDescription>
          </SheetHeader>

          {evidenceContext && (
            <EvidencePortfolio
              evidence={contextEvidence}
              kpaId={evidenceContext.kpaId}
              availableKPIs={availableKPIs}
              userRole="employee"
              currentUserId={employeeId}
              targetUserId={employeeId}
              onAddEvidence={onAddEvidence}
              onUpdateEvidence={onUpdateEvidence}
              onRefreshEvidence={onRefreshEvidence}
              onDeleteEvidence={onDeleteEvidence}
              onUploadDocument={onUploadDocument}
            />
          )}
        </SheetContent>
      </Sheet>
      </div>
  )
}
